<template>
  <div class="page">
    <div
      class="previous-screen"
      @click="router.push('/fundraisers')"
      data-test="back-button"
    >
      <img
        :src="PreviousArrow"
        alt="Back To Fundraiser Management"
        style="padding-right: 16px"
      />
      Back to Fundraiser Management
    </div>
    <FundraiserDetailsSkeleton
      v-if="!fundraiser"
      data-test="skeleton"
    ></FundraiserDetailsSkeleton>
    <div v-else>
      <div class="header-container">
        <div
          class="header"
          data-test="fundraiser-title"
        >
          {{ fundraiser.title }}
        </div>
        <Tag
          rounded
          class="status-badge"
          data-test="status-badge"
          :severity="mappedStatus"
          :value="fundraiser.status"
        ></Tag>
      </div>
      <div class="info-holder">
        <LeftColumn
          :editOpen="editOpen"
          data-test="left-column"
        />
        <RightColumn data-test="right-column" />
      </div>
      <FundraiserCreation
        v-if="fundraiserStore.success"
        :step="5"
        data-test="fundraiser-creation"
      ></FundraiserCreation>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import LeftColumn from '@/components/fundraisers/fundraiserDetails/LeftColumn.vue';
import RightColumn from '@/components/fundraisers/fundraiserDetails/RightColumn.vue';
import FundraiserCreation from './FundraiserCreation.vue';
import PreviousArrow from '@/assets/PreviousArrow.svg';
import FundraiserDetailsSkeleton from '@/components/fundraisers/Skeletons/FundraiserDetailsSkeleton.vue';

const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();
const route = useRoute();
const router = useRouter();

const fundraiserId = parseInt(route.params.idOrName, 10);
const fundraiser = ref(null);

const editOpen = ref(route.meta.edit);

const setFundraiser = async () => {
  await nextTick();
  fundraiser.value = donorStore.getFundraiserById(fundraiserId);
};

watch(
  () => donorStore.fundraisers,
  () => {
    setFundraiser();
  },
);

// // status mapping
const statusMapping = {
  Created: 'warn',
  Submitted: 'warn',
  Declined: 'danger',
  Deactivated: 'secondary',
  Published: 'success',
  Expired: 'secondary',
};

const mappedStatus = computed(() => {
  if (!fundraiser.value) {
    return 'unknown';
  }
  return statusMapping[fundraiser.value.status] || 'unknown';
});

const getUrlParameter = (name) => {
  name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};
onBeforeMount(async () => {
  if (getUrlParameter('created') === 'true') {
    fundraiserStore.success = true;
    fundraiserStore.domain = fundraiser.value.domain;
    fundraiserStore.urlLink = fundraiser.value.urlLink;
    fundraiserStore.title = fundraiser.value.title;
    await donorStore.fetchFundraisers();
  }
  if (donorStore.featureCompanyGiving && !companyAdminStore.companies.length) {
    await companyAdminStore.getCompanyCollection();
  }
  setFundraiser();
});
</script>
<style lang="scss" scoped>
.modal-body {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
}

.actions-icon {
  color: #000;
  font-family: 'Font Awesome 5 Pro';
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 120% */
}

.previous-screen {
  color: #000;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 114.286% */
  text-decoration-line: underline;
  display: flex;
  margin: 8px 0 20px 0;
  cursor: pointer;
}
.header {
  margin-bottom: 0px;
}
.header-container {
  display: flex;
  align-items: center; /* This will vertically center the items in the container */
  justify-content: flex-start; /* This will place the header on the left and the button on the right */
  margin-right: 0;
}
.info-holder {
  display: flex;
}

.status-badge {
  width: 135px;
  height: 20px;
  font-weight: 500;
  margin-top: 0 !important;
  flex-shrink: 0 !important;
  margin-left: 32px;
}

@media (max-width: 1288px) {
  .info-holder {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 1024px) {
  .info-holder {
    align-items: center;
    justify-content: space-around;
  }
  .previous-screen {
    justify-content: center;
  }
  .header {
    text-align: center; /* Centers the text horizontally */
    width: 100%; /* Ensures the div takes up the full width */
    margin: 0 auto; /* Centers the div within its parent container */
    padding: 20px 0; /* Adds some vertical padding for spacing */
  }
  .header-container {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .info-holder {
    flex-direction: column;
    align-items: center;
  }
}
</style>
